import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 18px" };

const REASON: Record<string, { label: string; bg: string; fg: string; icon: string }> = {
  sla_risk: { label: "SLA at risk", bg: "#fbe9e7", fg: "#b3261e", icon: "alert-triangle" },
  awaiting_pickup: { label: "Awaiting pickup", bg: "#e8f0f3", fg: "#2a6f8a", icon: "bell-ring" },
};

export function Attention() {
  // Refetches on mount/navigation via the app-wide react-query defaults (no
  // polling — consistent with every other screen; the list recomputes each visit).
  const { data, isLoading, error } = useQuery({ queryKey: ["attention"], queryFn: api.attention });
  if (isLoading) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  const items = data?.items ?? [];
  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Needs your action</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>Tickets that tripped a nudge rule — SLA at risk, a lapsed hold, or assigned to you and still untouched.</div>
      </div>

      {items.length === 0 ? (
        <div style={{ ...CARD, color: "#8a8e88", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="check-circle" size={16} color="#1f8a5b" /> Nothing needs your attention right now.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((t) => (
            <Link
              key={t.id}
              to="/tickets/$ticketId"
              params={{ ticketId: t.id }}
              style={{ ...CARD, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, textDecoration: "none", color: "inherit" }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  <span style={{ fontFamily: "'Space Mono',monospace", fontWeight: 700 }}>{t.ref}</span> {t.subject}
                </div>
                <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 2 }}>{t.clientId} · {t.priority}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {t.reasons.map((r) => {
                  const meta = REASON[r] ?? { label: r, bg: "#f0efe9", fg: "#6b706a", icon: "circle" };
                  return (
                    <span key={r} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: meta.bg, color: meta.fg }}>
                      <Icon name={meta.icon} size={11} color={meta.fg} /> {meta.label}
                    </span>
                  );
                })}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Page>
  );
}
